import { keepPreviousData, useInfiniteQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useSearchParams } from 'react-router-dom';

import { Answer } from '@/components/common/Answer';
import { AsOfStrip } from '@/components/common/AsOfStrip';
import { ExplainerCard } from '@/components/common/ExplainerCard';
import { ExportButton } from '@/components/common/ExportButton';
import { FieldGuide } from '@/components/common/FieldGuide';
import { EmptyState, ErrorMessage, SkeletonLoader } from '@/components/common/StateViews';
import { useAutoLoadMore } from '@/hooks/useAutoLoadMore';
import { fetchIbamaCases, ibamaCaseParams } from '@/services/ibama';
import type { IbamaCaseRow, IbamaFilterOption } from '@/services/types';

const PAGE_SIZE = 50;
const CNPJ_DIGITS = 14;

/**
 * Every auto in SICAFI's debt ledger, filtered.
 *
 * The ledger is the population with a published outcome; autos that never
 * entered collection are not here and the overview says how many there are.
 * The five status axes above the table are five different processes and
 * disagree by design, so no single "resolved" figure is shown. Natural
 * persons are listed as unnamed rows: nothing about them is loaded.
 *
 * Every filter seeds from the URL, because the overview and the company
 * profile link here with a filter already applied. Rows accumulate as the
 * reader scrolls; `keepPreviousData` keeps the previous total on screen while
 * a narrowed query is in flight, and `data-stale` says so.
 */
export function IbamaCasesPage() {
  const { t, i18n } = useTranslation(['ibama', 'common']);
  const [seed] = useSearchParams();
  const [draft, setDraft] = useState(seed.get('query') ?? '');
  const [query, setQuery] = useState<string | null>(seed.get('query') || null);
  const [uf, setUf] = useState(seed.get('uf') ?? '');
  const [nature, setNature] = useState(seed.get('nature') ?? '');
  const [penaltyType, setPenaltyType] = useState(seed.get('penalty_type') ?? '');
  const [outcome, setOutcome] = useState(seed.get('outcome') ?? '');
  const [instance, setInstance] = useState(seed.get('instance') ?? '');
  const [year, setYear] = useState(seed.get('year') ?? '');
  const [subject, setSubject] = useState(seed.get('subject') ?? '');
  const [currency, setCurrency] = useState(seed.get('currency') ?? '');
  const [entity, setEntity] = useState(seed.get('entity') ?? '');

  const filters = {
    query,
    uf: uf || null,
    nature: nature || null,
    penalty_type: penaltyType || null,
    outcome: outcome || null,
    instance: instance || null,
    year: year ? Number(year) : null,
    subject: subject || null,
    currency: currency || null,
    entity: entity || null,
  };

  const {
    data: paged,
    isPending,
    error,
    refetch,
    isPlaceholderData,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
  } = useInfiniteQuery({
    queryKey: ['ibama', 'cases', filters],
    queryFn: ({ pageParam }) =>
      fetchIbamaCases({ ...filters, limit: PAGE_SIZE, offset: pageParam }),
    initialPageParam: 0,
    getNextPageParam: (lastPage, allPages) => {
      const loaded = allPages.reduce((n, page) => n + page.items.length, 0);
      return loaded < lastPage.total ? loaded : undefined;
    },
    placeholderData: keepPreviousData,
  });

  const data = paged?.pages[0];
  const items = paged?.pages.flatMap((page) => page.items) ?? [];

  const { sentinelRef, scrollRef, loadMore } = useAutoLoadMore({
    hasNextPage,
    isFetchingNextPage,
    fetchNextPage,
  });

  const number = new Intl.NumberFormat(i18n.language);
  const percent = new Intl.NumberFormat(i18n.language, {
    style: 'percent',
    maximumFractionDigits: 1,
  });

  const options = data?.options ?? {};
  const outcomeLabel = (option: IbamaFilterOption) =>
    t(`ibama:overview.outcomes.${option.value}`, { defaultValue: option.value });

  return (
    <div className="space-y-4 p-6">
      <header className="space-y-1">
        <h1 className="text-lg font-semibold text-foreground">{t('ibama:cases.title')}</h1>
        <p className="text-sm text-muted-foreground">{t('ibama:cases.subtitle')}</p>
      </header>

      <ExplainerCard i18nKey="ibama:cases.explainer" />

      <div className="flex justify-end">
        <ExportButton path={`/api/v1/ibama/export/cases?${ibamaCaseParams(filters).toString()}`} />
      </div>

      {data ? <AsOfStrip asOf={data.as_of} /> : null}

      <form
        className="flex flex-wrap items-end gap-3 rounded-lg border bg-card p-3"
        onSubmit={(event) => {
          event.preventDefault();
          setQuery(draft.trim() || null);
        }}
      >
        <div className="min-w-[16rem] flex-1 space-y-1">
          <label htmlFor="ibama-case-query" className="text-xs font-medium text-foreground">
            {t('ibama:cases.search.label')}
          </label>
          <input
            id="ibama-case-query"
            value={draft}
            onChange={(event) => setDraft(event.target.value)}
            placeholder={t('ibama:cases.search.placeholder')}
            className="w-full rounded-md border bg-background px-3 py-1.5 text-sm text-foreground"
          />
        </div>
        <Select
          id="ibama-uf"
          label={t('ibama:cases.filters.uf')}
          any={t('ibama:cases.filters.any')}
          value={uf}
          onChange={setUf}
          options={options.uf}
        />
        <Select
          id="ibama-nature"
          label={t('ibama:cases.filters.nature')}
          any={t('ibama:cases.filters.any')}
          value={nature}
          onChange={setNature}
          options={options.nature}
        />
        <Select
          id="ibama-penalty-type"
          label={t('ibama:cases.filters.penaltyType')}
          any={t('ibama:cases.filters.any')}
          value={penaltyType}
          onChange={setPenaltyType}
          options={options.penalty_type}
        />
        <Select
          id="ibama-outcome"
          label={t('ibama:cases.filters.outcome')}
          any={t('ibama:cases.filters.any')}
          value={outcome}
          onChange={setOutcome}
          options={options.outcome}
          render={outcomeLabel}
        />
        <Select
          id="ibama-instance"
          label={t('ibama:cases.filters.instance')}
          any={t('ibama:cases.filters.any')}
          value={instance}
          onChange={setInstance}
          options={options.instance}
          render={(option) =>
            t(`ibama:cases.filters.instance_${option.value}`, { defaultValue: option.value })
          }
        />
        <Select
          id="ibama-year"
          label={t('ibama:cases.filters.year')}
          any={t('ibama:cases.filters.any')}
          value={year}
          onChange={setYear}
          options={options.year}
        />
        <Select
          id="ibama-subject"
          label={t('ibama:cases.filters.subject')}
          any={t('ibama:cases.filters.any')}
          value={subject}
          onChange={setSubject}
          options={options.subject}
          render={(option) =>
            t(`ibama:cases.filters.subject_${option.value}`, { defaultValue: option.value })
          }
        />
        <Select
          id="ibama-currency"
          label={t('ibama:cases.filters.currency')}
          any={t('ibama:cases.filters.any')}
          value={currency}
          onChange={setCurrency}
          options={options.currency}
          render={(option) =>
            t(`ibama:cases.filters.currency_${option.value}`, { defaultValue: option.value })
          }
        />
        <button
          type="submit"
          className="rounded-md bg-primary px-3 py-1.5 text-sm font-medium text-primary-foreground"
        >
          {t('ibama:cases.search.submit')}
        </button>
      </form>

      {entity ? (
        <div className="flex items-center gap-2 text-xs text-muted-foreground" data-entity-chip>
          <span>{t('ibama:cases.filters.entityChip', { cnpj: entity })}</span>
          <button
            type="button"
            onClick={() => setEntity('')}
            className="rounded border px-2 py-0.5 text-foreground"
          >
            {t('ibama:cases.filters.clear')}
          </button>
        </div>
      ) : null}

      {data ? (
        <div className="grid gap-4 lg:grid-cols-2">
          <section className="rounded-lg border bg-card p-4" data-status-axes>
            <h2 className="mb-2 text-sm font-semibold text-foreground">
              {t('ibama:cases.axes.title')}
            </h2>
            <Answer answer={data.status_axes} help={t('ibama:cases.axes.help')}>
              {(axes) => (
                <dl className="grid grid-cols-2 gap-2 text-sm sm:grid-cols-3">
                  {(
                    [
                      ['firstDecided', axes.first_decided, ibamaCasesHref({ instance: 'first' })],
                      ['boardDated', axes.board_dated, ibamaCasesHref({ instance: 'board' })],
                      ['settled', axes.settled, ibamaCasesHref({ outcome: 'SETTLED' })],
                      ['inCourt', axes.in_court, ibamaCasesHref({ outcome: 'JUDICIAL' })],
                      ['writtenOff', axes.written_off, ibamaCasesHref({ outcome: 'WRITTEN_OFF' })],
                    ] as const
                  ).map(([key, value, href]) => (
                    <div key={key} className="rounded-md border bg-muted/30 p-2" data-axis={key}>
                      <dt className="text-xs text-muted-foreground">
                        {t(`ibama:cases.axes.${key}`)}
                      </dt>
                      <dd className="font-semibold tabular-nums text-foreground">
                        <Link to={href} className="text-primary underline-offset-2 hover:underline">
                          {number.format(value)}
                        </Link>
                        <span className="ml-1 text-xs font-normal text-muted-foreground">
                          ({percent.format(axes.total_cases ? value / axes.total_cases : 0)})
                        </span>
                      </dd>
                    </div>
                  ))}
                </dl>
              )}
            </Answer>
            <div className="mt-3">
              <FieldGuide i18nKey="ibama:cases.guide.axes" />
            </div>
          </section>

          <section className="rounded-lg border bg-card p-4" data-concentration>
            <h2 className="mb-2 text-sm font-semibold text-foreground">
              {t('ibama:cases.concentration.title')}
            </h2>
            <Answer answer={data.concentration} help={t('ibama:cases.concentration.help')}>
              {(c) => (
                <div className="space-y-2 text-sm">
                  <p className="text-xs text-muted-foreground">
                    {t('ibama:cases.concentration.summary', {
                      entities: number.format(c.entities),
                      cases: number.format(c.cases),
                      single: percent.format(c.single_case_share),
                      persons: number.format(c.person_cases),
                    })}
                  </p>
                  <ul className="grid grid-cols-5 gap-2">
                    {c.bands.map((band) => (
                      <li
                        key={band.label}
                        className="rounded-md border bg-muted/30 p-2"
                        data-band={band.label}
                      >
                        <div className="text-xs text-muted-foreground">
                          {t('ibama:cases.concentration.band', { band: band.label })}
                        </div>
                        <div className="font-semibold tabular-nums text-foreground">
                          {number.format(band.entities)}
                        </div>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </Answer>
          </section>
        </div>
      ) : null}

      {isPending ? (
        <SkeletonLoader rows={8} />
      ) : error ? (
        <ErrorMessage message={error.message} onRetry={() => void refetch()} />
      ) : items.length === 0 ? (
        <EmptyState hint={t('ibama:cases.empty')} />
      ) : (
        <section className="rounded-lg border bg-card" data-stale={isPlaceholderData || undefined}>
          <div className="flex items-center justify-between px-4 py-2 text-xs text-muted-foreground">
            <span data-total={data?.total}>
              {t('ibama:cases.count', {
                loaded: number.format(items.length),
                total: number.format(data?.total ?? 0),
              })}
            </span>
          </div>
          <div ref={scrollRef} className="max-h-[70vh] overflow-auto">
            <table className="w-full text-sm">
              <thead className="sticky top-0 z-10 bg-muted text-left text-xs text-muted-foreground">
                <CaseTableHead />
              </thead>
              <tbody>
                {items.map((row) => (
                  <CaseTableRow key={row.key} row={row} />
                ))}
              </tbody>
            </table>
            <div ref={sentinelRef} />
          </div>
          {hasNextPage ? (
            <div className="border-t px-4 py-2 text-center">
              <button
                type="button"
                onClick={loadMore}
                disabled={isFetchingNextPage}
                className="text-sm text-primary underline-offset-2 hover:underline"
              >
                {isFetchingNextPage ? t('common:state.loading') : t('ibama:cases.loadMore')}
              </button>
            </div>
          ) : null}
        </section>
      )}
    </div>
  );
}

export function CaseTableHead() {
  const { t } = useTranslation(['ibama']);
  return (
    <tr>
      <th className="px-3 py-2 font-medium">{t('ibama:cases.columns.auto')}</th>
      <th className="px-3 py-2 font-medium">{t('ibama:cases.columns.subject')}</th>
      <th className="px-3 py-2 font-medium">{t('ibama:cases.columns.nature')}</th>
      <th className="px-3 py-2 font-medium">{t('ibama:cases.columns.noticeDate')}</th>
      <th className="px-3 py-2 font-medium">{t('ibama:cases.columns.instances')}</th>
      <th className="px-3 py-2 text-right font-medium">{t('ibama:cases.columns.faceValue')}</th>
      <th className="px-3 py-2 font-medium">{t('ibama:cases.columns.collection')}</th>
    </tr>
  );
}

/**
 * One ledger row. A natural person renders as an unnamed subject, never as a
 * blank: the absence is the rule, not a missing value.
 */
export function CaseTableRow({ row }: { row: IbamaCaseRow }) {
  const { t, i18n } = useTranslation(['ibama']);
  const number = new Intl.NumberFormat(i18n.language, { maximumFractionDigits: 2 });
  const currency = new Intl.NumberFormat(i18n.language, {
    style: 'currency',
    currency: 'BRL',
    maximumFractionDigits: 0,
  });
  return (
    <tr className="border-t align-top" data-case={row.key} data-subject={row.entity_type ?? ''}>
      <td className="px-3 py-2">
        <Link
          to={`/ibama/c/${row.key}`}
          className="font-medium tabular-nums text-primary underline-offset-4 hover:underline"
        >
          {row.notice_number}
        </Link>
        <div className="text-xs text-muted-foreground">
          {row.uf}
          {row.municipality ? ` · ${row.municipality}` : ''}
        </div>
      </td>
      <td className="px-3 py-2">
        {row.entity_type === 'company' &&
        row.entity_cnpj &&
        row.entity_cnpj.length === CNPJ_DIGITS ? (
          <Link
            to={`/ibama/e/${row.entity_cnpj}`}
            className="line-clamp-2 text-primary underline-offset-4 hover:underline"
          >
            {row.entity_name ?? row.entity_cnpj}
          </Link>
        ) : (
          <span className="italic text-muted-foreground">
            {row.entity_type === 'person'
              ? t('ibama:cases.subject.person')
              : t('ibama:cases.subject.unknown')}
          </span>
        )}
      </td>
      <td className="px-3 py-2 text-muted-foreground">
        {row.nature ?? '—'}
        {row.penalty_type ? <div className="text-xs">{row.penalty_type}</div> : null}
      </td>
      <td className="px-3 py-2 tabular-nums text-muted-foreground">{row.notice_date ?? '—'}</td>
      <td className="px-3 py-2 text-xs text-muted-foreground">
        <div>
          {t('ibama:cases.instances.first')}: {row.first_decision_date ?? '—'}
        </div>
        <div>
          {t('ibama:cases.instances.board')}: {row.board_decision_date ?? '—'}
        </div>
      </td>
      <td className="px-3 py-2 text-right tabular-nums text-foreground">
        {row.original_amount === null
          ? '—'
          : row.in_brl
            ? currency.format(row.original_amount)
            : `${number.format(row.original_amount)} ${row.currency ?? ''}`}
      </td>
      <td className="px-3 py-2 text-muted-foreground">
        {row.collection_outcome
          ? t(`ibama:overview.outcomes.${row.collection_outcome}`, {
              defaultValue: row.collection_outcome,
            })
          : '—'}
        {row.credit_status ? <div className="text-xs">{row.credit_status}</div> : null}
      </td>
    </tr>
  );
}

function ibamaCasesHref(filters: Parameters<typeof ibamaCaseParams>[0]): string {
  const params = ibamaCaseParams(filters).toString();
  return params ? `/ibama/cases?${params}` : '/ibama/cases';
}

function Select({
  id,
  label,
  any,
  value,
  onChange,
  options,
  render,
}: {
  id: string;
  label: string;
  any: string;
  value: string;
  onChange: (value: string) => void;
  options?: IbamaFilterOption[];
  render?: (option: IbamaFilterOption) => string;
}) {
  const { i18n } = useTranslation();
  const number = new Intl.NumberFormat(i18n.language);
  return (
    <div className="space-y-1">
      <label htmlFor={id} className="block text-xs font-medium text-foreground">
        {label}
      </label>
      <select
        id={id}
        value={value}
        onChange={(event) => onChange(event.target.value)}
        className="max-w-[13rem] rounded-md border bg-background px-2 py-1.5 text-sm text-foreground"
      >
        <option value="">{any}</option>
        {(options ?? []).map((option) => (
          <option key={option.value} value={option.value}>
            {render ? render(option) : option.value} ({number.format(option.cases)})
          </option>
        ))}
      </select>
    </div>
  );
}
